import type { MemoEntry, UUID } from "@/lib/types";

/**
 * Whose line is this? — the id to attribute an entry to, or null for no attribution.
 *
 * Kills the "ghost content" problem: a draft fills up between meetings and nobody can tell
 * who put what there, so a line that should be discussed reads as if it had always been part
 * of the document.
 *
 * 🔴 ONLY on lines written in THIS meeting (`carried_from_entry_id === null`).
 * `create_next_memo` stamps `created_by` on every carried entry with THE PERSON WHO CREATED
 * THE DRAFT — so attributing a carried line to its `created_by` would credit them for
 * something a colleague wrote weeks ago. That is worse than no attribution: it is confidently
 * wrong, in a record people rely on. The carried lines were already discussed in the meeting
 * they came from; they are not the ghosts.
 *
 * 🔴 DRAFT ONLY. Once finalized, the room has seen it — the line is no longer "something that
 * appeared", it is minutes. The Änderungsprotokoll holds the actor trail from then on.
 *
 * An `update` line is skipped because it ALREADY renders its author inline (that attribution
 * is why it is read-only), and a second avatar would say the same thing twice.
 */
export function attributionFor(
  entry: Pick<MemoEntry, "created_by" | "carried_from_entry_id" | "kind">,
  opts: { finalized: boolean },
): UUID | null {
  if (opts.finalized) return null;
  if (entry.kind === "update") return null;
  if (entry.carried_from_entry_id) return null;
  return entry.created_by ?? null;
}
